/**
 * Client-side mirror of `backend/services/strategy_match.py`.
 *
 * IMPORTANT: keep this file in lockstep with the Python engine. The
 * backend is the source of truth (bulk jobs, analysis endpoint); this
 * JS copy exists only so the dashboard can compute Strategy Match on
 * visible properties instantly without re-hitting the API.
 */

const CRITERION_WEIGHTS = {
  budget: 15,
  property_type: 10,
  location: 10,
  min_cash_flow: 25,
  min_coc: 20,
  min_cap_rate: 15,
  risk_tolerance: 5,
};

const LABELS = {
  budget: "Budget",
  property_type: "Property Type",
  location: "Target Location",
  min_cash_flow: "Min Cash Flow",
  min_coc: "Min Cash-on-Cash",
  min_cap_rate: "Min Cap Rate",
  risk_tolerance: "Risk Tolerance",
};

const fmt$ = (n) => `$${Math.round(n).toLocaleString()}`;

const graded = (actual, minimum) => {
  if (actual >= minimum) return [1.0, "pass"];
  if (minimum <= 0) return [0.0, "fail"];
  const ratio = Math.max(actual, 0) / minimum;
  if (ratio >= 0.7) return [Math.round(ratio * 1000) / 1000, "partial"];
  return [0.0, "fail"];
};

const normRisk = (er, vac) => {
  if (er <= 0) return 0.5;
  const erScore = Math.max(0, Math.min(1, (60 - er) / 30));
  const vacScore = Math.max(0, Math.min(1, (12 - vac) / 8));
  return erScore * 0.75 + vacScore * 0.25;
};

/** Returns true when a criteria object has at least one scoreable field. */
export function hasCriteria(criteria) {
  if (!criteria || typeof criteria !== "object") return false;
  return Boolean(
    criteria.budget_max || criteria.budget_min ||
    criteria.min_cash_flow != null || criteria.min_coc != null ||
    criteria.min_cap_rate != null ||
    (criteria.property_types || []).length ||
    (criteria.locations || []).length ||
    criteria.risk_tolerance
  );
}

/**
 * Score how well an analysis fits investor criteria. Returns null when
 * no scoreable criteria are set. Mirrors evaluate_strategy_match().
 */
export function evaluateStrategyMatch(analysis, criteria) {
  if (!analysis || !hasCriteria(criteria)) return null;
  const prop = analysis.property || {};
  const price = Number(analysis.purchase_price || prop.price || 0);
  const cashFlow = Number(analysis.cash_flow_monthly || 0);
  const coc = Number(analysis.cash_on_cash_return || 0);
  const cap = Number(analysis.cap_rate || 0);

  const rows = [];
  let includedWeight = 0;
  let total = 0;
  const add = (criterion, status, credit, detail) => {
    const w = CRITERION_WEIGHTS[criterion];
    includedWeight += w;
    total += credit * w;
    rows.push({ criterion, label: LABELS[criterion], status, detail, weight: w });
  };

  const { budget_max, budget_min } = criteria;
  if (budget_max || budget_min) {
    if (budget_max && price > budget_max) {
      if (price <= budget_max * 1.05) add("budget", "partial", 0.5, `${fmt$(price)} is slightly over your ${fmt$(budget_max)} max (within 5%)`);
      else add("budget", "fail", 0, `${fmt$(price)} exceeds your ${fmt$(budget_max)} max`);
    } else if (budget_min && price < budget_min) {
      add("budget", "fail", 0, `${fmt$(price)} is below your ${fmt$(budget_min)} minimum`);
    } else {
      add("budget", "pass", 1, `${fmt$(price)} fits your budget`);
    }
  }

  const types = criteria.property_types || [];
  if (types.length) {
    const ptype = String(prop.property_type || "").trim().toLowerCase();
    const title = (ptype || "unknown").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    if (types.includes(ptype)) add("property_type", "pass", 1, `${title} is on your list`);
    else add("property_type", "fail", 0, `${title} isn't in your target types`);
  }

  const locations = criteria.locations || [];
  if (locations.length) {
    const hay = ["address", "city", "state", "zip_code"].map((k) => String(prop[k] || "")).join(" ").toLowerCase();
    let matched = null;
    for (const loc of locations) {
      const tokens = String(loc).replace(/,/g, " ").split(/\s+/).map((t) => t.trim().toLowerCase()).filter(Boolean);
      if (tokens.length && tokens.every((t) => hay.includes(t))) { matched = loc; break; }
    }
    if (matched) add("location", "pass", 1, `In your target market (${matched})`);
    else add("location", "fail", 0, "Outside your target markets");
  }

  if (criteria.min_cash_flow != null && criteria.min_cash_flow !== "") {
    const min = Number(criteria.min_cash_flow);
    const [credit, status] = graded(cashFlow, min);
    add("min_cash_flow", status, credit, `${fmt$(cashFlow)}/mo vs your ${fmt$(min)}/mo minimum`);
  }
  if (criteria.min_coc != null && criteria.min_coc !== "") {
    const min = Number(criteria.min_coc);
    const [credit, status] = graded(coc, min);
    add("min_coc", status, credit, `${coc.toFixed(1)}% vs your ${min.toFixed(1)}% minimum`);
  }
  if (criteria.min_cap_rate != null && criteria.min_cap_rate !== "") {
    const min = Number(criteria.min_cap_rate);
    const [credit, status] = graded(cap, min);
    add("min_cap_rate", status, credit, `${cap.toFixed(1)}% vs your ${min.toFixed(1)}% minimum`);
  }

  if (criteria.risk_tolerance) {
    let er = Number(analysis.expense_ratio || 0);
    if (!er) {
      const egi = Number(analysis.effective_gross_income || 0);
      const totExp = Number(analysis.total_monthly_expenses || 0);
      er = egi > 0 ? (totExp / egi) * 100 : 0;
    }
    const vac = Number((analysis.zip_data || {}).vacancy_rate ?? 6);
    const riskNorm = normRisk(er, vac);
    const thresholds = { low: [0.6, 0.45], medium: [0.4, 0.25], high: [0, 0] };
    const [p, part] = thresholds[criteria.risk_tolerance] || [0, 0];
    if (riskNorm >= p) add("risk_tolerance", "pass", 1, `Risk profile fits your ${criteria.risk_tolerance} tolerance`);
    else if (riskNorm >= part) add("risk_tolerance", "partial", 0.5, `Borderline for your ${criteria.risk_tolerance} risk tolerance`);
    else add("risk_tolerance", "fail", 0, `Riskier than your ${criteria.risk_tolerance} tolerance allows`);
  }

  if (!includedWeight) return null;
  const score = Math.round((total / includedWeight) * 100);
  const verdict = score >= 80 ? "strong_match" : score >= 55 ? "partial_match" : "weak_match";
  return { match_score: score, verdict, breakdown: rows, criteria_count: rows.length };
}
